import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RotateCw } from 'lucide-react';
import type { PermissionKey } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAccess } from '@/lib/access/use-access';
import { useAccessChangedRefresh, useRefreshOnForbidden } from '@/lib/access/use-access-live';
import { homePath } from '@/lib/access/route-permissions';
import { NoAccessAssigned, PermissionDenied } from '@/routes/forbidden';

// Route-layer access gates (users-roles-permissions R5, brief §5 C1/P8). Today's UI hid
// links but every route opened by URL and gated pages rendered BLANK (§3); now:
//
//   <AccessBoundary>      pathless layout route around every console route. Holds the
//                         route content until `/v1/me` answers (never a flash of
//                         "forbidden"), shows an error state with retry when the BFF is
//                         down, and the full "Sin acceso asignado" state for a member with
//                         no role. Keeps access live (WS `access.changed`, 403 re-check).
//   <RequirePermission>   per route: the screen, or the in-place "Acceso denegado".
//   <HomeRedirect>        `/` → the first surface the operator may open.

/** LOADING — the operator's access is being resolved. Same footprint as a route skeleton. */
export function AccessLoading() {
  const { t } = useTranslation('admin');
  return (
    <div
      className="space-y-3 p-4 sm:p-6"
      role="status"
      aria-busy="true"
      data-testid="access-loading"
    >
      <span className="sr-only">{t('access.loading')}</span>
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

/** ERROR — access could not be resolved (BFF down / access store unavailable). */
export function AccessError({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  const { t } = useTranslation('admin');
  return (
    <div
      className="flex min-h-full w-full items-center justify-center p-4 sm:p-6"
      data-testid="access-error"
    >
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" aria-hidden />
            {t('access.error.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground" role="alert">
            {t('access.error.description')}
          </p>
          <Button
            className="w-full sm:w-auto"
            onClick={onRetry}
            disabled={retrying}
            aria-busy={retrying}
          >
            <RotateCw className={retrying ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} aria-hidden />
            {t('access.error.retry')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/** Layout route: every console route renders through here. */
export function AccessBoundary() {
  const access = useAccess();
  useAccessChangedRefresh();
  useRefreshOnForbidden();

  if (access.loading) return <AccessLoading />;
  if (!access.ready) {
    return <AccessError onRetry={access.refresh} retrying={access.refreshing} />;
  }
  if (access.noAccess) {
    return <NoAccessAssigned onRetry={access.refresh} retrying={access.refreshing} />;
  }
  return <Outlet />;
}

/** One route: render it when the operator holds `permission`, else "Acceso denegado". */
export function RequirePermission({
  permission,
  children,
}: {
  permission: PermissionKey;
  children: ReactNode;
}) {
  const access = useAccess();
  if (access.loading) return <AccessLoading />;
  if (!access.can(permission)) return <PermissionDenied permission={permission} />;
  return <>{children}</>;
}

/** `/` — land on the first surface this operator may open (the profile if none). */
export function HomeRedirect() {
  const access = useAccess();
  if (access.loading) return <AccessLoading />;
  return <Navigate to={homePath(access.can)} replace />;
}
